import React, { useEffect, useMemo, useState } from "react";
import { FiSave } from "react-icons/fi";
import {
  LuBell,
  LuCheckCircle,
  LuRefreshCw,
  LuSettings,
  LuShieldCheck,
} from "react-icons/lu";
import { apiBool, apiString, normalizeApiStatus } from "./api/payoutApiAdapters";
import {
  createPayoutIdempotencyKey,
  isPayoutApiMode,
  payoutApi,
} from "./api/payoutApiClient";
import { usePayoutMutationGuard, usePayoutResource } from "./api/usePayoutResource";
import PayoutDataTable from "./PayoutDataTable";
import PayoutDropdown from "./PayoutDropdown";
import PayoutMfaSetup from "./PayoutMfaSetup";
import PayoutTableCell from "./PayoutTableCell";

type ApiRecord = Record<string, unknown>;

const statusClassName: Record<string, string> = {
  Enabled: "bg-[#EEF1F6] text-[#2F6B49]",
  Active: "bg-[#EEF1F6] text-[#2F6B49]",
  Processed: "bg-[#EEF1F6] text-[#2F6B49]",
  Stub: "bg-[#F3F4F6] text-[#505E8E]",
  Pending: "bg-[#F4F0EA] text-[#7A5C2E]",
  Disabled: "bg-[#F3F4F6] text-[#505E8E]",
  Inactive: "bg-[#F3F4F6] text-[#505E8E]",
};

const capabilityRowsFromPayload = (payload: ApiRecord) => {
  const rawCapabilities =
    payload.capabilities || payload.items || payload.data || payload.features;
  const rows = Array.isArray(rawCapabilities) ? rawCapabilities : [];
  return rows.map((item, index) => {
    const record = typeof item === "object" && item ? (item as ApiRecord) : {};
    return {
      capability: apiString(
        record,
        ["capability", "name", "key", "feature", "label"],
        `Capability ${index + 1}`,
      ),
      availability: normalizeApiStatus(
        apiString(record, ["availability", "status", "state"], "Enabled"),
        "Enabled",
      ),
      phase: apiString(
        record,
        ["phase", "release_phase", "releasePhase"],
        apiString(payload, ["release_phase", "releasePhase"], "Merchant payouts"),
      ),
      notes: apiString(record, ["notes", "description", "details"], "-"),
    };
  });
};

function ToggleSetting({
  label,
  description,
  checked,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <button
      type="button"
      onClick={() => onChange(!checked)}
      className="flex w-full items-center justify-between gap-5 rounded-[8px] border border-[#E0E5F2] bg-white px-4 py-3 text-left"
    >
      <span>
        <span className="block text-sm font-semibold text-[#1B163B]">{label}</span>
        <span className="mt-1 block text-xs font-medium text-[#505E8E]">
          {description}
        </span>
      </span>
      <span
        className={
          "relative h-6 w-11 shrink-0 rounded-full transition-colors " +
          (checked ? "bg-[#1B163B]" : "bg-[#DCE2EE]")
        }
      >
        <span
          className={
            "absolute top-1 h-4 w-4 rounded-full bg-white transition-transform " +
            (checked ? "left-6" : "left-1")
          }
        />
      </span>
    </button>
  );
}

function PayoutSettings() {
  const settingsResource = usePayoutResource<ApiRecord>(
    {},
    (signal) => payoutApi.settings(signal),
    [],
  );
  const capabilitiesResource = usePayoutResource<ApiRecord>(
    {},
    (signal) => payoutApi.capabilities(signal),
    [],
  );
  const mutationGuard = usePayoutMutationGuard();
  const [defaultMode, setDefaultMode] = useState("");
  const [referencePrefix, setReferencePrefix] = useState("");
  const [lowBalanceEmailEnabled, setLowBalanceEmailEnabled] = useState(false);
  const [settlementAlertsEnabled, setSettlementAlertsEnabled] = useState(false);
  const [approvalNotificationsEnabled, setApprovalNotificationsEnabled] =
    useState(false);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    const settings = settingsResource.data;
    setDefaultMode(apiString(settings, ["default_payment_mode", "defaultPaymentMode"], ""));
    setReferencePrefix(
      apiString(settings, ["payout_reference_prefix", "payoutReferencePrefix"], ""),
    );
    setLowBalanceEmailEnabled(
      apiBool(settings, ["low_balance_email_enabled", "lowBalanceEmailEnabled"], false),
    );
    setSettlementAlertsEnabled(
      apiBool(settings, ["settlement_alerts_enabled", "settlementAlertsEnabled"], false),
    );
    setApprovalNotificationsEnabled(
      apiBool(
        settings,
        ["approval_notifications_enabled", "approvalNotificationsEnabled"],
        false,
      ),
    );
  }, [settingsResource.data]);

  const capabilityRows = useMemo(() => {
    return capabilityRowsFromPayload(capabilitiesResource.data);
  }, [capabilitiesResource.data]);

  const saveSettings = async () => {
    setNotice("");
    setError("");
    const guard = mutationGuard(settingsResource.usingFallback);
    if (!guard.allowed) {
      setError(guard.reason);
      return;
    }
    setSaving(true);
    try {
      await payoutApi.updateSettings(
        {
          default_payment_mode: defaultMode,
          payout_reference_prefix: referencePrefix.trim(),
          low_balance_email_enabled: lowBalanceEmailEnabled,
          settlement_alerts_enabled: settlementAlertsEnabled,
          approval_notifications_enabled: approvalNotificationsEnabled,
        },
        createPayoutIdempotencyKey("settings", "merchant-payout-settings"),
      );
      settingsResource.refresh();
      setNotice("Settings saved successfully.");
    } catch (caughtError) {
      setError(
        caughtError instanceof Error
          ? caughtError.message
          : "Unable to save payout settings.",
      );
    } finally {
      setSaving(false);
    }
  };

  const refreshSettings = () => {
    settingsResource.refresh();
    capabilitiesResource.refresh();
  };

  return (
    <div className="payout-page-shell min-h-screen bg-[#EEF3F7] px-8 py-8 text-[#1B163B]">
      <div className="mb-7 flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-[24px] font-semibold leading-8">Settings</h1>
          <p className="mt-1 text-sm font-medium text-[#3F4F86]">
            Configure payout defaults, notifications and backend capabilities.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={refreshSettings}
            className="flex h-10 items-center gap-2 rounded-[6px] border border-[#E0E5F2] bg-white px-4 text-sm font-semibold text-[#1B163B]"
          >
            <LuRefreshCw className="h-4 w-4" />
            Refresh
          </button>
          <button
            type="button"
            onClick={saveSettings}
            disabled={saving || !isPayoutApiMode()}
            style={{ backgroundColor: "#1B163B", color: "#FFFFFF" }}
            className="flex h-10 items-center gap-2 rounded-[6px] px-5 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-60"
            title={!isPayoutApiMode() ? "Switch to API mode to save settings." : undefined}
          >
            <FiSave className="h-4 w-4" />
            {saving ? "Saving..." : "Save Settings"}
          </button>
        </div>
      </div>

      {(notice || error || settingsResource.usingFallback || capabilitiesResource.usingFallback) && (
        <div
          className={
            "mb-5 rounded-[8px] border px-4 py-3 text-sm font-semibold " +
            (error
              ? "border-[#F0D1D1] bg-[#FFF7F7] text-[#8A3A3A]"
              : "border-[#E0E5F2] bg-white text-[#505E8E]")
          }
        >
          {error ||
            notice ||
            "API response is unavailable or empty, so this screen is showing fallback data."}
        </div>
      )}

      <div className="mb-5 grid gap-4 [grid-template-columns:repeat(auto-fit,minmax(220px,1fr))]">
        <div className="flex min-h-[118px] items-center gap-4 rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
          <span className="payout-kpi-icon flex h-12 w-12 items-center justify-center rounded-full bg-[#6687FF] text-xl text-white">
            <LuSettings />
          </span>
          <div>
            <p className="text-xs font-semibold text-[#505E8E]">Data Source</p>
            <p className="mt-2 text-[22px] font-semibold leading-7 text-[#1B163B]">
              {isPayoutApiMode() ? "API" : "Mock"}
            </p>
          </div>
        </div>
        <div className="flex min-h-[118px] items-center gap-4 rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
          <span className="payout-kpi-icon flex h-12 w-12 items-center justify-center rounded-full bg-[#6687FF] text-xl text-white">
            <LuShieldCheck />
          </span>
          <div>
            <p className="text-xs font-semibold text-[#505E8E]">Step-Up Verification</p>
            <p className="mt-2 text-[22px] font-semibold leading-7 text-[#1B163B]">
              {apiBool(settingsResource.data, ["step_up_enabled", "stepUpEnabled"], true)
                ? "Enabled"
                : "Disabled"}
            </p>
          </div>
        </div>
        <div className="flex min-h-[118px] items-center gap-4 rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
          <span className="payout-kpi-icon flex h-12 w-12 items-center justify-center rounded-full bg-[#6687FF] text-xl text-white">
            <LuCheckCircle />
          </span>
          <div>
            <p className="text-xs font-semibold text-[#505E8E]">Capabilities</p>
            <p className="mt-2 text-[22px] font-semibold leading-7 text-[#1B163B]">
              {capabilityRows.length}
            </p>
          </div>
        </div>
      </div>

      <div className="mb-5 grid gap-5 lg:grid-cols-[minmax(0,1fr)_360px]">
        <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
          <h2 className="text-lg font-semibold text-[#1B163B]">Payout Defaults</h2>
          <p className="mt-1 text-sm font-medium text-[#505E8E]">
            These values are sent to the backend only in API mode.
          </p>
          <div className="mt-5 grid gap-4 md:grid-cols-2">
            <PayoutDropdown
              label="Default Payment Mode"
              value={defaultMode}
              options={["NEFT", "RTGS", "IMPS"]}
              onChange={setDefaultMode}
              className="w-full"
            />
            <label className="block">
              <span className="mb-2 block text-xs font-semibold text-[#505E8E]">
                Reference Prefix
              </span>
              <input
                value={referencePrefix}
                onChange={(event) => setReferencePrefix(event.target.value.toUpperCase())}
                maxLength={12}
                className="h-10 w-full rounded-[6px] border border-[#E0E5F2] bg-white px-3 text-sm font-semibold text-[#1B163B] outline-none focus:border-[#6687FF]"
              />
            </label>
          </div>
          <div className="mt-5 grid gap-3">
            <ToggleSetting
              label="Low balance email alerts"
              description="Notify finance users when a funding account balance crosses the threshold."
              checked={lowBalanceEmailEnabled}
              onChange={setLowBalanceEmailEnabled}
            />
            <ToggleSetting
              label="Settlement alerts"
              description="Send transfer settlement status changes to authorized users."
              checked={settlementAlertsEnabled}
              onChange={setSettlementAlertsEnabled}
            />
            <ToggleSetting
              label="Approval notifications"
              description="Notify checkers and approvers when a payout needs action."
              checked={approvalNotificationsEnabled}
              onChange={setApprovalNotificationsEnabled}
            />
          </div>
        </section>

        <section className="rounded-[8px] border border-[#E0E5F2] bg-white p-5 new-shadow">
          <h2 className="text-lg font-semibold text-[#1B163B]">Security</h2>
          <div className="mt-4 space-y-4 text-sm font-medium text-[#505E8E]">
            <p>
              API mode uses Authorization, X-School-Id, X-Correlation-Id and
              idempotency headers through the shared payouts client.
            </p>
            <p>
              Mutations are blocked whenever the read layer falls back to mock data,
              so demo content is not treated as live money truth.
            </p>
          </div>
          <div className="mt-5">
            <PayoutMfaSetup />
          </div>
          <div className="mt-5 rounded-[8px] bg-[#EEF3F7] p-4 text-sm font-semibold text-[#3F4F86]">
            <LuBell className="mb-2 h-5 w-5 text-[#6687FF]" />
            Hidden nav route. Keep available for backend QA at
            <span className="ml-1 text-[#1B163B]">/payouts/settings</span>.
          </div>
        </section>
      </div>

      <PayoutDataTable
        title="Backend Capabilities"
        columns={["Capability", "Availability", "Phase", "Notes"]}
        rows={capabilityRows.map((row) => [
          <PayoutTableCell
            value={row.capability}
            valueClassName="font-semibold text-[#1B163B]"
          />,
          <span
            className={
              "inline-flex rounded-[5px] px-2 py-1 text-xs font-semibold " +
              (statusClassName[row.availability] || "bg-[#F3F4F6] text-[#505E8E]")
            }
          >
            {row.availability}
          </span>,
          row.phase,
          <PayoutTableCell
            value={row.notes}
            valueClassName="text-sm font-medium text-[#505E8E]"
            maxWidthClassName="max-w-[420px]"
          />,
        ])}
        minWidth="min-w-[820px]"
      />
    </div>
  );
}

export default PayoutSettings;
